// Creator API service
import api from '../../services/api';

// Types
export interface Creator {
  creator_id: number;
  email: string;
  name: string;
  bio: string | null;
  website_url: string | null;
  social_links: Record<string, string> | null;
  status: 'pending' | 'approved' | 'active' | 'suspended' | 'rejected';
  promo_code: string | null;
  code_uses: number | null;
  payment_method: string | null;
  payment_details: Record<string, string> | null;
  created_at: string;
}

export interface CreatorDashboard {
  creator: {
    creator_id: number;
    name: string;
    email: string;
    status: string;
    promo_code: string;
    code_uses: number;
    terms_accepted_at: string | null;
  };
  membership: {
    status: string;
    start_date: string;
    end_date: string;
  } | null;
  referrals: {
    total: number;
    trialing: number;
    active: number;
    cancelled: number;
    churned: number;
    new_this_month: number;
  };
  revenue: {
    total_revenue: number;
    total_commission: number;
    total_earned: number;
    pending_payout: number;
    total_paid: number;
  };
}

export interface Referral {
  referral_id: number;
  user_id: number;
  user_email: string;
  signup_date: string;
  subscription_status: 'trialing' | 'active' | 'cancelled' | 'churned';
  first_paid_date: string | null;
  lifetime_revenue: number;
  lifetime_commission: number;
}

export interface Earning {
  earning_id: number;
  period_start: string;
  period_end: string;
  active_referrals: number;
  new_referrals: number;
  churned_referrals: number;
  gross_revenue: number;
  platform_fees: number;
  net_revenue: number;
  commission_rate: number;
  commission_earned: number;
  payout_status: 'pending' | 'processing' | 'paid';
}

export interface Payout {
  payout_id: number;
  amount: number;
  period_start: string;
  period_end: string;
  payment_method: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payment_reference: string | null;
  created_at: string;
  completed_at: string | null;
}

// API calls

export const getCreatorProfile = async (): Promise<Creator> => {
  const response = await api.get('/creators/me');
  return response.data.creator;
};

export const getCreatorDashboard = async (): Promise<CreatorDashboard> => {
  const response = await api.get('/creators/me/dashboard');
  return response.data.dashboard;
};

export const getCreatorReferrals = async (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<Referral[]> => {
  const response = await api.get('/creators/me/referrals', { params });
  return response.data.referrals;
};

export const getCreatorEarnings = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<Earning[]> => {
  const response = await api.get('/creators/me/earnings', { params });
  return response.data.earnings;
};

export const getCreatorPayouts = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<Payout[]> => {
  const response = await api.get('/creators/me/payouts', { params });
  return response.data.payouts;
};

export const updatePaymentDetails = async (
  paymentMethod: string,
  paymentDetails: Record<string, string>
): Promise<void> => {
  await api.put('/creators/me/payment-details', {
    payment_method: paymentMethod,
    payment_details: paymentDetails,
  });
};

export const requestPayout = async (): Promise<Payout> => {
  const response = await api.post('/creators/me/request-payout');
  return response.data.payout;
};

export const requestMembershipExtension = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/creators/me/request-extension');
  return response.data;
};

export const acceptCreatorTerms = async (): Promise<{ success: boolean; terms_accepted_at: string }> => {
  const response = await api.post('/creators/me/accept-terms');
  return response.data;
};
